import { buildSchedule } from './schedule.js';
import { Scenario } from './types.js';

/** Upper bound of the search, percent a year. Above it the answer is reported as unknown. */
export const MAX_EQUIVALENT_RATE_APR = 100;

/** Step of the upward scan for calendar loans (see equivalentRateApr). */
const CALENDAR_SCAN_STEP_APR = 1;

const HALVINGS = 24; // 100 / 2^24 ≈ 6e-6 %, well inside the 0.01 % shown

/** The same loan with every event removed (fresh object, input untouched). */
export function withoutEvents(scenario: Scenario): Scenario {
  return { ...scenario, ratePeriods: [], overpayments: [], refinances: [] };
}

/** True when the scenario carries a discounted period, an overpayment or a refinance. */
export function hasEvents(scenario: Scenario): boolean {
  return scenario.ratePeriods.length > 0 || scenario.overpayments.length > 0 || scenario.refinances.length > 0;
}

function plainInterest(plain: Scenario, rateApr: number): number {
  return buildSchedule({ ...plain, rateApr }).months.reduce((sum, m) => sum + m.interestPortion, 0);
}

/**
 * Annual rate (percent, two decimals) at which `withoutEvents(scenario)` pays `totalInterest`
 * (cents). The plain monthly loan's interest is non-decreasing in the rate, so a bisection
 * converges (calendar loans: see the bracket below): the scenario's own rate when it has no events (plain equals actual), 0 for
 * a target of zero or less, `null` when even MAX_EQUIVALENT_RATE_APR does not reach the target.
 */
export function equivalentRateApr(scenario: Scenario, totalInterest: number): number | null {
  if (!hasEvents(scenario)) return scenario.rateApr;
  if (totalInterest <= 0) return 0;
  const plain = withoutEvents(scenario);
  let lo = 0;
  let hi = MAX_EQUIVALENT_RATE_APR;
  if (scenario.calculationMode === 'bank_calendar') {
    // The 31-day payment floor makes calendar interest non-monotonic at high rates (a higher
    // payment shortens the term; the peak can be as low as ~15 %). Scan up from 0 in 1 % steps
    // to the first rate that reaches the target, then bisect that step: the lowest matching
    // rate. Past the peak the answer is an approximation.
    hi = CALENDAR_SCAN_STEP_APR;
    while (plainInterest(plain, hi) < totalInterest) {
      if (hi >= MAX_EQUIVALENT_RATE_APR) return null;
      lo = hi;
      hi = Math.min(hi + CALENDAR_SCAN_STEP_APR, MAX_EQUIVALENT_RATE_APR);
    }
  } else if (plainInterest(plain, MAX_EQUIVALENT_RATE_APR) < totalInterest) {
    return null;
  }
  for (let i = 0; i < HALVINGS; i++) {
    const mid = (lo + hi) / 2;
    if (plainInterest(plain, mid) < totalInterest) lo = mid;
    else hi = mid;
  }
  return Math.round(((lo + hi) / 2) * 100) / 100;
}
